import {useLogout} from '@common/auth/requests/logout';
import {useResendVerificationEmail} from '@common/auth/requests/use-resend-verification-email';
import {
  useValidateEmailVerificationOtp,
  ValidateEmailVerificationOtpPayload,
} from '@common/auth/requests/use-validate-email-verification-otp';
import {useUser} from '@common/auth/ui/use-user';
import {Button} from '@ui/buttons/button';
import {Form} from '@ui/forms/form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {message} from '@ui/i18n/message';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {KeyboardArrowLeftIcon} from '@ui/icons/material/KeyboardArrowLeft';
import {FullPageLoader} from '@ui/progress/full-page-loader';
import {useSettings} from '@ui/settings/use-settings';
import {useIsDarkMode} from '@ui/themes/use-is-dark-mode';
import {useForm} from 'react-hook-form';
import {createBrowserRouter} from 'react-router';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useState} from 'react';

export const verifyEmailRouter = createBrowserRouter([
  {
    path: '*',
    element: <EmailVerificationPage />,
    hydrateFallbackElement: <FullPageLoader screen />,
  },
]);

export function EmailVerificationPage() {
  const {trans} = useTrans();
  const {data} = useUser('me');
  const resendEmail = useResendVerificationEmail();
  const {
    branding: {logo_light, logo_dark},
  } = useSettings();
  const isDarkMode = useIsDarkMode();
  const logoSrc = isDarkMode ? logo_light : logo_dark;
  const logout = useLogout();

  const form = useForm<ValidateEmailVerificationOtpPayload>();
  const validateOtp = useValidateEmailVerificationOtp(form);

  return (
    <div className="flex min-h-screen w-screen bg-alt p-24">
      <div className="mx-auto mt-40 max-w-440">
        <Button
          variant="outline"
          onClick={() => logout.mutate()}
          startIcon={<KeyboardArrowLeftIcon />}
          size="xs"
          className="mb-54 mr-auto"
        >
          <Trans message="Logout" />
        </Button>
        {logoSrc && (
          <img
            src={logoSrc}
            alt="Site logo"
            className="mx-auto mb-44 block h-42 w-auto"
          />
        )}
        <div className="text-center">
          <h1 className="mb-24 text-3xl">
            <Trans message="Verify your email" />
          </h1>
          <h2 className="text-lg">
            <Trans
              message="Enter the verification code we sent to :email"
              values={{email: maskEmailAddress(data?.user.email)}}
            />
          </h2>
          <Form
            form={form}
            onSubmit={values => validateOtp.mutate(values)}
            className="my-16"
          >
            <FormTextField
              name="code"
              label={<Trans message="Code" />}
              placeholder={trans(message('Enter your verification code'))}
              autoFocus
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="off"
              maxLength={6}
              inputMode="numeric"
              required
            />
            <Button
              type="submit"
              variant="flat"
              color="primary"
              size="md"
              className="mt-24 w-full"
              disabled={validateOtp.isPending}
            >
              <Trans message="Next" />
            </Button>
          </Form>
          <WhatsappInstead user={data?.user as any} />
          <div className="mb-24 text-sm">
            <Trans
              message="If you don't see the email in your inbox, check your spam folder and promotions tab. If you still don't see it, <a>request a resend</a>."
              values={{
                a: text => (
                  <Button
                    variant="link"
                    color="primary"
                    disabled={resendEmail.isPending || !data?.user.email}
                    onClick={() => {
                      resendEmail.mutate({email: data!.user.email});
                    }}
                  >
                    {text}
                  </Button>
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function maskEmailAddress(email: string | undefined) {
  if (!email) return '*******************';
  const [username, domain] = email.split('@');
  return `${username.slice(0, 2)}****@${domain}`;
}

/**
 * One confirmed way to reach the student is enough to sign in (owner,
 * 2026-09-21): a WhatsApp number confirmed with a code passes this screen as
 * well as an email does. So a student whose email is wrong, old, or simply not
 * theirs any more is not stuck here — they confirm the number on their
 * account instead, and fix the email later from their profile.
 */
function WhatsappInstead({user}: {user?: {phone_e164?: string | null; phone?: string | null}}) {
  const {trans} = useTrans();
  const accepts = !!(getBootstrapData() as any)?.settings?.verification_accepts_verified_phone;
  const phone = user?.phone_e164 || (user?.phone ? `+${String(user.phone).replace(/\D/g, '')}` : null);
  const {data: options} = useQuery({
    queryKey: ['login-code', 'options'],
    queryFn: () => apiClient.get<{whatsapp?: boolean}>('login-code/options').then(r => r.data),
    enabled: accepts && !!phone,
  });
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState<{challenge: string; masked: string} | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!accepts || !phone || !options?.whatsapp) return null;

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e?.response?.data?.message || trans(message('Something went wrong. Please try again.')));
    } finally {
      setBusy(false);
    }
  };
  const send = () =>
    run(async () => {
      const res = await apiClient.post('student/profile/phone/send-code', {on_file: true}).then(r => r.data);
      setSent(res);
      setCode('');
    });
  const verify = () =>
    run(async () => {
      await apiClient.post('student/profile/phone/verify', {challenge: sent!.challenge, code});
      // A fresh page load re-reads the account, which now has a confirmed number.
      window.location.reload();
    });

  return (
    <div className="mb-24 rounded-panel border bg-paper p-16 text-start text-sm">
      {!open ? (
        <Button variant="link" color="primary" onClick={() => setOpen(true)}>
          <Trans message="Can't reach this email? Confirm with WhatsApp instead" />
        </Button>
      ) : !sent ? (
        <>
          <p className="mb-12 text-muted">
            <Trans message="We will send a code on WhatsApp to the number on your account: :number" values={{number: maskPhone(phone)}} />
          </p>
          <Button variant="flat" color="primary" className="w-full" disabled={busy} onClick={send}>
            <Trans message="Send the code on WhatsApp" />
          </Button>
        </>
      ) : (
        <>
          <p className="mb-12 text-muted">
            <Trans message="Enter the 6-digit code we sent on WhatsApp to :number." values={{number: sent.masked}} />
          </p>
          <input
            className="w-full rounded-input border px-12 py-8 text-center text-lg tracking-widest"
            dir="ltr"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          />
          <Button variant="flat" color="primary" className="mt-12 w-full" disabled={busy || code.length < 6} onClick={verify}>
            <Trans message="Confirm" />
          </Button>
          <Button variant="link" className="mt-8" disabled={busy} onClick={send}>
            <Trans message="Send a new code" />
          </Button>
        </>
      )}
      {error && <p className="mt-8 text-danger">{error}</p>}
    </div>
  );
}

function maskPhone(e164: string) {
  const d = e164.replace(/\D/g, '');
  return `+${d.slice(0, 2)} ••• ••• ${d.slice(-4)}`;
}
